import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, ArrowRight, ShieldAlert, Loader2 } from "lucide-react";
import { getReport } from "../governance/govShare";
import { savePdf, policySentence, computeQa, deployCategory } from "../governance/govPdf";
import { FRAMEWORKS, DEPLOY_QUESTIONS, SKJOLD_LABELS } from "../governance/govData";
import Radar from "../components/governance/Radar";
import { Button } from "../components/ui/button";

const Card = ({ title, children }) => (
  <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
    <p className="text-xs uppercase tracking-[0.16em] text-primary">{title}</p>
    <div className="mt-3">{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex flex-col">
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
    <dd className="mt-0.5 text-sm text-foreground">{v}</dd>
  </div>
);

export default function SharedReport() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => { document.title = "Shared governance report | norse3"; }, []);
  useEffect(() => {
    let live = true;
    getReport(token)
      .then((data) => { if (live) { setState(data.state || {}); setStatus("ready"); } })
      .catch(() => { if (live) setStatus("notfound"); });
    return () => { live = false; };
  }, [token]);

  if (status === "loading") {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading report…</div>;
  }
  if (status === "notfound") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5">
        <ShieldAlert className="h-10 w-10 text-signal-amber" />
        <h1 className="mt-4 text-2xl font-bold text-foreground">Report not found</h1>
        <p className="mt-2 text-muted-foreground">This link may be incorrect or the report is no longer available.</p>
        <Button asChild className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/governance-tools">Open the governance tools</Link></Button>
      </div>
    );
  }

  const { result, policy, qa, deploy, framework } = state;
  const skjold = state.skjold;
  const c = qa ? computeQa(qa) : null;
  const fwRows = framework ? FRAMEWORKS[framework] : null;

  return (
    <div className="mx-auto max-w-4xl px-5 lg:px-8 py-12 lg:py-16">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-white/10 pb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-primary">Shared governance report</p>
          <h1 className="mt-1.5 font-heading text-2xl sm:text-3xl font-bold text-foreground">norse3 governance snapshot</h1>
          <p className="mt-1 text-sm text-muted-foreground">Educational only — not legal, regulatory or compliance advice.</p>
        </div>
        <Button onClick={() => savePdf.snapshot(state)} data-testid="shared-download-pdf" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
          <Download className="mr-1.5 h-4 w-4" /> Download PDF
        </Button>
      </div>

      <div className="mt-8 space-y-6">
        {result && (
          <Card title="Readiness assessment">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">{result.band.band}</span>
              <div className="text-right"><p className="font-heading text-3xl font-bold text-foreground">{result.overall}</p><p className="text-[0.7rem] text-muted-foreground">index / 100</p></div>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{result.band.blurb}</p>
            <Radar values={result.dims} />
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <p className="text-xs font-semibold text-signal-green">Strongest</p>
                <ul className="mt-1.5 space-y-1 text-sm text-muted-foreground">{result.strongest.length ? result.strongest.map((d) => <li key={d}>{d}</li>) : <li>—</li>}</ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-signal-amber">Gaps</p>
                <ul className="mt-1.5 space-y-1 text-sm text-muted-foreground">{result.gaps.length ? result.gaps.map((d) => <li key={d}>{d}</li>) : <li>—</li>}</ul>
              </div>
            </div>
          </Card>
        )}

        {skjold && (
          <Card title="Document controls">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${skjold === "in_place" ? "border-signal-green/30 bg-signal-green/10 text-signal-green" : skjold === "planned" ? "border-signal-amber/30 bg-signal-amber/10 text-signal-amber" : "border-signal-red/30 bg-signal-red/10 text-signal-red"}`}>Skjold: {SKJOLD_LABELS[skjold]}</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Skjold scans documents locally before they are shared with AI tools and blocks restricted material such as passports, bank statements, IDs and proof of address.</p>
          </Card>
        )}

        {policy && (
          <Card title="Policy control">
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              <KV k="Trigger" v={policy.trigger} />
              <KV k="User type" v={policy.userType} />
              <KV k="Channel" v={policy.channel} />
              <KV k="Severity" v={policy.severity} />
              <KV k="Action" v={policy.action} />
              <KV k="Evidence" v={policy.evidence?.length ? policy.evidence.join(", ") : "None"} />
            </dl>
            <p className="mt-4 text-sm text-muted-foreground">{policySentence(policy)}</p>
          </Card>
        )}

        {qa && c && (
          <Card title="QA coverage & resource model">
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              <KV k="Monthly interactions" v={qa.interactions.toLocaleString("en-GB")} />
              <KV k="Currently reviewed" v={`${qa.pctReviewed}%`} />
              <KV k="Manual review hours" v={Math.round(c.manualHours).toLocaleString("en-GB")} />
              <KV k="Estimated manual QA cost" v={`£${Math.round(c.manualCost).toLocaleString("en-GB")}`} />
              <KV k="Automated screening coverage" v={`${qa.targetCoverage}%`} />
              <KV k="Indicative reviewer-hour difference" v={`${Math.round(c.hoursDiff).toLocaleString("en-GB")} hrs/month`} />
            </dl>
          </Card>
        )}

        {deploy && Object.keys(deploy).length > 0 && (
          <Card title="Deployment configuration">
            <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">{deployCategory(deploy)}</span>
            <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {DEPLOY_QUESTIONS.filter((q) => deploy[q.id]).map((q) => <KV key={q.id} k={q.text} v={deploy[q.id]} />)}
            </dl>
          </Card>
        )}

        {framework && fwRows && (
          <Card title={`Framework mapping — ${framework}`}>
            <div className="space-y-3">
              {fwRows.map((r) => (
                <div key={r.theme} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <p className="text-sm font-semibold text-foreground">{r.theme}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{r.question}</p>
                  <p className="mt-1 text-xs text-primary">norse3: {r.control}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Link to="/governance-tools">Run the tools yourself</Link></Button>
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Book a norse3 walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
      </div>
    </div>
  );
}
